// src/pages/Orders.js
import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./styles/orders.css";

import product1 from "../images/products/stamp.jpg";

// ✅ thunks from userSlice.js
import {
  getOrders,
  cancelMyOrder,
  addProdToCart,
  getUserCart,
} from "../app/features/user/userSlice";

/* =========================
   ✅ HELPERS
========================= */
const formatUGX = (n) => `UGX ${Number(n || 0).toLocaleString()}`;
const safeText = (v) => String(v ?? "").trim();

const normalizeImg = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  return val?.url || "";
};

const getProductPath = (slugOrId) => `/product/${encodeURIComponent(String(slugOrId || ""))}`;

const getFirstProductImage = (p) => {
  if (!p) return "";
  if (Array.isArray(p?.images) && p.images.length) return normalizeImg(p.images[0]);
  return normalizeImg(p?.image);
};

const getOrderItemSlugOrId = (item) => {
  const prodObj =
    item?.productSnapshot && typeof item.productSnapshot === "object"
      ? item.productSnapshot
      : item?.product && typeof item.product === "object"
      ? item.product
      : null;

  const slug =
    safeText(prodObj?.slug) ||
    safeText(item?.productSlug) ||
    safeText(item?.slug);

  if (slug) return slug;

  const id =
    prodObj?._id ||
    prodObj?.id ||
    item?.productId ||
    item?.product?._id ||
    item?.product ||
    item?.id ||
    "";

  return safeText(id);
};

const pickOrderItemImage = (item) => {
  const fromVariant = normalizeImg(item?.variantImage);
  if (fromVariant) return fromVariant;

  const prodObj =
    item?.productSnapshot && typeof item.productSnapshot === "object"
      ? item.productSnapshot
      : item?.product && typeof item.product === "object"
      ? item.product
      : null;

  const fromProd = getFirstProductImage(prodObj);
  return fromProd || product1;
};

/* =========================
   ✅ GUEST CART HELPERS
========================= */
const GUEST_CART_KEY = "kupto_guest_cart_v1";

const readGuestCart = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

const writeGuestCart = (list) => {
  const safe = Array.isArray(list) ? list : [];
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(safe));
  window.dispatchEvent(new Event("guestCartUpdated"));
};

const buildGuestKey = (x) => {
  const pid = String(x?.productId || "");
  const size = x?.size ? String(x.size) : "";
  const color = x?.color ? String(x.color) : "";
  const side = x?.printSide ? String(x.printSide) : "";
  return `${pid}__${size}__${color}__${side}`;
};

const Orders = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id: orderParamId } = useParams();
  const location = useLocation();

  // ✅ allow /my-orders/:id OR /my-orders?id=xxx OR route state
  const qsOrderId = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("id") || "";
  }, [location.search]);

  const stateOrderId = safeText(location?.state?.selectedOrderId || "");
  const orderId = safeText(orderParamId || qsOrderId || stateOrderId);

  const authState = useSelector((state) => state?.auth || {});
  const user = authState?.user || null;

  const isLoggedIn = Boolean(user?.token || localStorage.getItem("token"));

  // ✅ slice flags
  const isLoading = Boolean(authState?.isLoading);
  const isError = Boolean(authState?.isError);
  const message = authState?.message;

  // ✅ THIS is where your getOrders thunk stores list
  const ordersList = useMemo(() => {
    const a = authState?.getOrderedProduct || [];
    return Array.isArray(a) ? a : [];
  }, [authState?.getOrderedProduct]);

  // ✅ local UI state for cancelling
  const [cancellingId, setCancellingId] = useState("");

  // ✅ local UI state for reorder
  const [reorderingId, setReorderingId] = useState("");

  // ✅ load orders list (backend)
  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    dispatch(getOrders());
  }, [dispatch, isLoggedIn, navigate]);

  // ✅ pick the order to display:
  // - if orderId exists -> find it in ordersList
  // - else -> show latest order
  const order = useMemo(() => {
    if (!ordersList.length) return null;

    if (orderId) {
      const wanted = String(orderId).trim();

      const exact =
        ordersList.find(
          (o) =>
            String(o?._id || "").trim() === wanted ||
            String(o?.id || "").trim() === wanted ||
            String(o?.orderId || "").trim() === wanted ||
            String(o?.orderNumber || "").trim() === wanted
        ) || null;

      if (exact) return exact;
    }

    // latest (by createdAt if present)
    const sorted = [...ordersList].sort((a, b) => {
      const ad = new Date(a?.createdAt || 0).getTime();
      const bd = new Date(b?.createdAt || 0).getTime();
      return bd - ad;
    });

    return sorted[0] || null;
  }, [ordersList, orderId]);

  const orderItems = useMemo(() => {
    const arr = order?.orderItems || order?.items || [];
    return Array.isArray(arr) ? arr : [];
  }, [order?.orderItems, order?.items]);

  const shippingInfo = order?.shippingInfo || {};
  const paymentInfo = order?.paymentInfo || {};

  const orderNumber = useMemo(() => {
    const n =
      order?.orderNumber ||
      order?.orderId ||
      order?.reference ||
      order?._id ||
      order?.id ||
      "";
    return n ? `#${String(n).replace(/^#/, "")}` : "—";
  }, [order?.orderNumber, order?.orderId, order?.reference, order?._id, order?.id]);

  const paymentMethodLabel = useMemo(() => {
    const pm = safeText(paymentInfo?.paymentMethod || order?.paymentMethod);
    if (!pm) return "—";
    if (pm === "cashOnDelivery") return "Cash on Delivery";
    if (pm === "mobileMoney") return "Mobile Money";
    if (pm === "airtelMoney") return "Airtel Money";
    if (pm === "mtnMomo") return "MTN MoMo";
    if (pm === "card") return "Card";
    if (pm === "paypal") return "PayPal";
    return pm;
  }, [paymentInfo?.paymentMethod, order?.paymentMethod]);

  const transactionId = useMemo(() => {
    return (
      safeText(paymentInfo?.transactionId) ||
      safeText(paymentInfo?.txId) ||
      safeText(order?.transactionId) ||
      "—"
    );
  }, [paymentInfo?.transactionId, paymentInfo?.txId, order?.transactionId]);

  const deliveryDate = useMemo(() => {
    const d = order?.estimatedDeliveryDate || order?.deliveryDate || order?.deliveredAt || null;
    if (!d) return "—";
    try {
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return String(d);
      return dt.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return String(d);
    }
  }, [order?.estimatedDeliveryDate, order?.deliveryDate, order?.deliveredAt]);

  // ✅ totals (prefer backend values your checkout sends)
  const itemsTotal = useMemo(() => {
    const v = order?.itemsTotal ?? order?.totalItemsPrice ?? null;
    if (v !== null && v !== undefined && String(v).trim() !== "") return Number(v || 0);

    return orderItems.reduce((sum, x) => {
      const qty = Number(x?.quantity || x?.qty || 1) || 1;
      const unit = Number(x?.unitPrice || x?.price || 0) || 0;
      return sum + unit * qty;
    }, 0);
  }, [order?.itemsTotal, order?.totalItemsPrice, orderItems]);

  const setupFeeTotal = useMemo(() => {
    const v = order?.setupFeeTotal ?? order?.setupFee ?? 0;
    return Number(v || 0) || 0;
  }, [order?.setupFeeTotal, order?.setupFee]);

  const shipping = useMemo(() => {
    const v = order?.shippingPrice ?? order?.shipping ?? 0;
    return Number(v || 0) || 0;
  }, [order?.shippingPrice, order?.shipping]);

  const taxes = useMemo(() => {
    const v = order?.taxPrice ?? order?.taxes ?? 0;
    return Number(v || 0) || 0;
  }, [order?.taxPrice, order?.taxes]);

  const couponDiscount = useMemo(() => {
    const v = order?.couponDiscount ?? order?.discount ?? 0;
    return Number(v || 0) || 0;
  }, [order?.couponDiscount, order?.discount]);

  const total = useMemo(() => {
    const v = order?.totalPrice ?? order?.totalPriceAfterDiscount ?? null;
    if (v !== null && v !== undefined && String(v).trim() !== "") return Number(v || 0);
    return Math.max(0, itemsTotal + setupFeeTotal + shipping + taxes - couponDiscount);
  }, [
    order?.totalPrice,
    order?.totalPriceAfterDiscount,
    itemsTotal,
    setupFeeTotal,
    shipping,
    taxes,
    couponDiscount,
  ]);

  // ✅ Recent orders list (from backend list)
  const recentOrders = useMemo(() => {
    const sorted = [...ordersList].sort((a, b) => {
      const ad = new Date(a?.createdAt || 0).getTime();
      const bd = new Date(b?.createdAt || 0).getTime();
      return bd - ad;
    });
    return sorted.slice(0, 5);
  }, [ordersList]);

  const viewOrder = (oid) => {
    const id = String(oid || "").trim();
    if (!id) return;

    navigate(`/my-orders/${id}`, {
      state: { selectedOrderId: id },
    });
  };

  const reorder = useCallback(
    async (oid) => {
      const selectedOrder =
        ordersList.find((o) => String(o?._id || o?.id || "") === String(oid)) || null;

      if (!selectedOrder) return;

      const selectedItems = Array.isArray(selectedOrder?.orderItems)
        ? selectedOrder.orderItems
        : Array.isArray(selectedOrder?.items)
        ? selectedOrder.items
        : [];

      if (!selectedItems.length) return;

      try {
        setReorderingId(String(oid));

        if (isLoggedIn) {
          for (const it of selectedItems) {
            const productId =
              it?.product?._id ||
              it?.productSnapshot?._id ||
              it?.productId ||
              it?.product ||
              "";

            if (!productId) continue;

            const payload = {
              productId: String(productId),
              quantity: Math.max(1, Number(it?.quantity || it?.qty || 1)),
              price: Number(it?.unitPrice || it?.price || 0) || 0,
            };

            const colorId = it?.color?._id || it?.color || "";
            const sizeId = it?.size?._id || it?.size || "";

            if (colorId) payload.color = colorId;
            if (sizeId) payload.size = sizeId;

            if (Array.isArray(it?.uploadedFiles) && it.uploadedFiles.length) {
              payload.uploadedFiles = it.uploadedFiles;
            }

            if (it?.instruction) payload.instruction = String(it.instruction);
            if (it?.printSide) payload.printSide = String(it.printSide);

            if (it?.printUnitPrice !== undefined) {
              payload.printUnitPrice = Number(it.printUnitPrice || 0);
            }
            if (it?.printKey) payload.printKey = String(it.printKey || "");
            if (it?.printPricingTitle) {
              payload.printPricingTitle = String(it.printPricingTitle || "");
            }
            if (it?.preparePriceOnce !== undefined) {
              payload.preparePriceOnce = Number(it.preparePriceOnce || 0);
            }
            if (it?.preparePriceApplied !== undefined) {
              payload.preparePriceApplied = Boolean(it.preparePriceApplied);
            }

            const variantImage =
              typeof it?.variantImage === "string"
                ? it.variantImage
                : it?.variantImage?.url || "";

            if (variantImage) payload.variantImage = variantImage;

            // eslint-disable-next-line no-await-in-loop
            await dispatch(addProdToCart(payload)).unwrap();
          }

          await dispatch(getUserCart()).unwrap();
        } else {
          const existing = readGuestCart();

          const incoming = selectedItems
            .map((it) => {
              const productId =
                it?.product?._id ||
                it?.productSnapshot?._id ||
                it?.productId ||
                it?.product ||
                "";

              if (!productId) return null;

              const colorId = it?.color?._id || it?.color || "";
              const sizeId = it?.size?._id || it?.size || "";

              const guestItem = {
                productId: String(productId),
                quantity: Math.max(1, Number(it?.quantity || it?.qty || 1)),
                price: Number(it?.unitPrice || it?.price || 0) || 0,

                name:
                  it?.productSnapshot?.title ||
                  it?.product?.title ||
                  it?.title ||
                  it?.name ||
                  "Product",

                color: colorId || "",
                colorLabel:
                  it?.color?.title || it?.color?.name || String(it?.color || "").trim(),

                size: sizeId || "",
                sizeLabel:
                  it?.size?.title || it?.size?.name || String(it?.size || "").trim(),

                printSide: it?.printSide || "",
                printUnitPrice: Number(it?.printUnitPrice || 0) || 0,
                printPricingTitle: String(it?.printPricingTitle || ""),
                printKey: String(it?.printKey || ""),
                preparePriceOnce: Number(it?.preparePriceOnce || 0) || 0,
                preparePriceApplied: Boolean(it?.preparePriceApplied),

                uploadedFiles: Array.isArray(it?.uploadedFiles) ? it.uploadedFiles : [],
                instruction: it?.instruction || "",

                variantImage:
                  typeof it?.variantImage === "string"
                    ? it.variantImage
                    : it?.variantImage?.url || "",

                viewedAt: Date.now(),
              };

              guestItem.key = buildGuestKey(guestItem);
              return guestItem;
            })
            .filter(Boolean);

          writeGuestCart([...existing, ...incoming]);
        }

        navigate("/cart");
      } catch (err) {
        console.error("Reorder failed:", err);
      } finally {
        setReorderingId("");
      }
    },
    [dispatch, isLoggedIn, navigate, ordersList]
  );

  // ✅ cancellation rules (safe defaults)
  const orderStatus = useMemo(
    () => safeText(order?.orderStatus || order?.status),
    [order?.orderStatus, order?.status]
  );

  const canCancelThisOrder = useMemo(() => {
    if (!order) return false;

    const st = orderStatus.toLowerCase();
    if (!st) return true;

    if (st === "cancelled" || st === "canceled") return false;
    if (st === "delivered") return false;

    return true;
  }, [order, orderStatus]);

  const cancelOrderNow = useCallback(async () => {
    const oid = safeText(order?._id || order?.id);
    if (!oid) return;

    const ok = window.confirm(
      "Cancel this order?\n\nThis action will mark the order as cancelled."
    );
    if (!ok) return;

    try {
      setCancellingId(oid);
      await dispatch(cancelMyOrder(oid)).unwrap();
      await dispatch(getOrders()).unwrap();
    } catch (e) {
      console.error(e);
    } finally {
      setCancellingId("");
    }
  }, [dispatch, order]);

  const showLoading = isLoading && !order;
  const showEmpty = !showLoading && !order && !isError;

  return (
    <div className="oc-page">
      {/* HERO */}
      <div className="oc-hero">
        <h1 className="oc-title">Order Completed</h1>
        <p className="oc-bread">
          Home <span>/</span> Order Completed
        </p>
      </div>

      <div className="oc-shell">
        {/* SUCCESS ICON + TEXT */}
        <div className="oc-success">
          <div className="oc-check">
            <span>✓</span>
          </div>

          <h2 className="oc-successTitle">Your order is completed!</h2>
          <p className="oc-successSub">
            {showLoading
              ? "Loading your order..."
              : isError
              ? message || "Failed to load order."
              : "Thank you. Your Order has been received."}
          </p>

          {showEmpty ? (
            <div style={{ marginTop: 10, color: "#777", fontSize: 13 }}>
              No order found to display.{" "}
              <Link to="/my-orders" style={{ fontWeight: 800 }}>
                Go to My Orders
              </Link>
            </div>
          ) : null}
        </div>

        {/* SUMMARY BAR */}
        <div className="oc-summaryBar">
          <div className="oc-sItem">
            <div className="oc-sLabel">Order ID</div>
            <div className="oc-sValue">{orderNumber}</div>
          </div>

          <div className="oc-sItem">
            <div className="oc-sLabel">Payment Method</div>
            <div className="oc-sValue">{paymentMethodLabel}</div>
          </div>

          <div className="oc-sItem">
            <div className="oc-sLabel">Transaction ID</div>
            <div className="oc-sValue">{transactionId}</div>
          </div>

          <div className="oc-sItem">
            <div className="oc-sLabel">Estimated Delivery Date</div>
            <div className="oc-sValue">{deliveryDate}</div>
          </div>

          {/* ✅ CANCEL BUTTON */}
          <button
            type="button"
            className="oc-invoiceBtn"
            onClick={cancelOrderNow}
            disabled={!canCancelThisOrder || !order || Boolean(cancellingId)}
            title={
              !order
                ? "No order"
                : !canCancelThisOrder
                ? "This order cannot be cancelled"
                : cancellingId
                ? "Cancelling..."
                : "Cancel this order"
            }
            style={{
              marginLeft: 10,
              opacity: !canCancelThisOrder ? 0.6 : 1,
            }}
          >
            {cancellingId
              ? "Cancelling..."
              : orderStatus && orderStatus.toLowerCase() === "cancelled"
              ? "Cancelled"
              : "Cancel Order"}
          </button>
        </div>

        {/* ORDER DETAILS CARD */}
        <div className="oc-card">
          <div className="oc-cardHead">Order Details</div>

          <div className="oc-table">
            <div className="oc-tHead">
              <div className="oc-th">Products</div>
              <div className="oc-th oc-th--right">Sub Total</div>
            </div>

            <div className="oc-tBody">
              {showLoading ? (
                <div style={{ padding: 18, color: "#777", fontSize: 13 }}>
                  Loading items...
                </div>
              ) : orderItems.length ? (
                orderItems.map((it, idx) => {
                  const qty = Number(it?.quantity || it?.qty || 1) || 1;

                  const prodObj =
                    it?.productSnapshot && typeof it.productSnapshot === "object"
                      ? it.productSnapshot
                      : it?.product && typeof it.product === "object"
                      ? it.product
                      : null;

                  const name =
                    safeText(prodObj?.title || prodObj?.name || it?.title || it?.name) ||
                    "Item";

                  const color =
                    safeText(it?.color?.title || it?.color?.name || it?.colorLabel || it?.color) ||
                    "";

                  const size =
                    safeText(it?.size?.title || it?.size?.name || it?.sizeLabel || it?.size) ||
                    "";

                  const unitPrice = Number(it?.unitPrice || it?.price || 0) || 0;
                  const lineTotal = unitPrice * qty;

                  const printSide = safeText(it?.printSide);
                  const printPricingTitle = safeText(it?.printPricingTitle);
                  const printUnitPrice = Number(it?.printUnitPrice || 0) || 0;

                  const img = pickOrderItemImage(it);
                  const productSlugOrId = getOrderItemSlugOrId(it);

                  return (
                    <div className="oc-row" key={it?._id || it?.id || `${name}-${idx}`}>
                      <div className="oc-prod">
                        <div
                          className="oc-img"
                          style={{ cursor: productSlugOrId ? "pointer" : "default" }}
                          onClick={() =>
                            productSlugOrId ? navigate(getProductPath(productSlugOrId)) : null
                          }
                        >
                          <img src={img} alt={name} />
                        </div>

                        <div className="oc-prodText">
                          <div
                            className="oc-name"
                            style={{ cursor: productSlugOrId ? "pointer" : "default" }}
                            onClick={() =>
                              productSlugOrId ? navigate(getProductPath(productSlugOrId)) : null
                            }
                          >
                            {name}
                          </div>

                          <div className="oc-meta">
                            {color ? (
                              <>
                                Color : <span>{color}</span>
                              </>
                            ) : null}
                            {color && size ? " | " : null}
                            {size ? (
                              <>
                                Size : <span>{size}</span>
                              </>
                            ) : null}

                            {(color || size) && qty ? " | " : null}
                            {qty ? (
                              <>
                                Qty : <span>{qty}</span>
                              </>
                            ) : null}
                          </div>

                          {(printPricingTitle || printSide || printUnitPrice > 0) && (
                            <div className="oc-meta" style={{ marginTop: 4 }}>
                              Printing :{" "}
                              <span style={{ fontWeight: 900 }}>
                                {printPricingTitle || "—"}
                              </span>
                              {printSide ? (
                                <span>
                                  {" "}
                                  | Side : <span>{printSide}</span>
                                </span>
                              ) : null}
                              {printUnitPrice > 0 ? (
                                <span>
                                  {" "}
                                  | Print unit : <span>{formatUGX(printUnitPrice)}</span>
                                </span>
                              ) : null}
                            </div>
                          )}

                          {Array.isArray(it?.uploadedFiles) && it.uploadedFiles.length ? (
                            <div className="oc-meta" style={{ marginTop: 4 }}>
                              Files : <span>{it.uploadedFiles.length}</span>
                            </div>
                          ) : null}

                          {it?.instruction ? (
                            <div className="oc-meta" style={{ marginTop: 4 }}>
                              Note : <span>{String(it.instruction)}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="oc-price">{formatUGX(lineTotal)}</div>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: 18, color: "#777", fontSize: 13 }}>
                  No items found.
                </div>
              )}
            </div>

            <div className="oc-tFoot">
              {/* Delivery / Contact */}
              <div style={{ padding: "14px 0 6px", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>Delivery</div>
                <div style={{ fontSize: 13, color: "#666", lineHeight: 1.6 }}>
                  <div>
                    <strong>Name:</strong>{" "}
                    {safeText(shippingInfo?.firstName) || safeText(shippingInfo?.lastName)
                      ? `${safeText(shippingInfo?.firstName)} ${safeText(
                          shippingInfo?.lastName
                        )}`.trim()
                      : "—"}
                  </div>
                  <div>
                    <strong>Phone:</strong> {safeText(shippingInfo?.phone) || "—"}
                  </div>
                  <div>
                    <strong>Email:</strong> {safeText(shippingInfo?.email) || "—"}
                  </div>
                  <div>
                    <strong>Address:</strong> {safeText(shippingInfo?.address) || "—"}
                    {safeText(shippingInfo?.region) ? ` • ${safeText(shippingInfo.region)}` : ""}
                    {safeText(shippingInfo?.subRegion)
                      ? ` • ${safeText(shippingInfo.subRegion)}`
                      : ""}
                  </div>
                  {order?.note ? (
                    <div>
                      <strong>Order note:</strong> {String(order.note)}
                    </div>
                  ) : null}

                  {/* ✅ show status clearly */}
                  {orderStatus ? (
                    <div style={{ marginTop: 6 }}>
                      <strong>Status:</strong>{" "}
                      <span style={{ fontWeight: 900 }}>{orderStatus}</span>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Totals */}
              <div className="oc-footRow">
                <span>Items subtotal</span>
                <strong>{formatUGX(itemsTotal)}</strong>
              </div>

              {setupFeeTotal > 0 ? (
                <div className="oc-footRow">
                  <span>Setup fee (once)</span>
                  <strong>{formatUGX(setupFeeTotal)}</strong>
                </div>
              ) : null}

              <div className="oc-footRow">
                <span>Shipping</span>
                <strong>{formatUGX(shipping)}</strong>
              </div>

              {taxes > 0 ? (
                <div className="oc-footRow">
                  <span>Taxes</span>
                  <strong>{formatUGX(taxes)}</strong>
                </div>
              ) : null}

              {couponDiscount > 0 ? (
                <div className="oc-footRow">
                  <span>Coupon Discount</span>
                  <strong>-{formatUGX(couponDiscount)}</strong>
                </div>
              ) : null}

              <div className="oc-footDivider" />

              <div className="oc-footRow oc-footRow--total">
                <span>Total</span>
                <strong>{formatUGX(total)}</strong>
              </div>

              <div className="oc-actions">
                <Link className="oc-backBtn" to="/product">
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* =========================
           RECENT ORDERS (BACKEND)
        ========================== */}
        <section className="oc-recent">
          <div className="oc-recentHead">
            <h2 className="oc-recentTitle">Recent Orders</h2>
            <Link to="/my-orders" className="oc-recentLink">
              View all
            </Link>
          </div>

          <div className="oc-recentCard">
            <div className="oc-rHead">
              <div>Order</div>
              <div>Date</div>
              <div>Status</div>
              <div className="oc-rRight">Total</div>
              <div className="oc-rRight">Action</div>
            </div>

            <div className="oc-rBody">
              {isLoading && !recentOrders.length ? (
                <div style={{ padding: 16, color: "#777", fontSize: 13 }}>
                  Loading recent orders...
                </div>
              ) : recentOrders.length ? (
                recentOrders.map((o) => {
                  const oid = o?._id || o?.id;

                  const dt = o?.createdAt ? new Date(o.createdAt) : null;
                  const dateLabel =
                    dt && !Number.isNaN(dt.getTime())
                      ? dt.toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                        })
                      : safeText(o?.date) || "—";

                  const status = safeText(o?.orderStatus || o?.status || "Processing");
                  const itemsCount = Array.isArray(o?.orderItems)
                    ? o.orderItems.reduce((a, x) => a + (Number(x?.quantity || 0) || 0), 0)
                    : Number(o?.items) || 0;

                  const method =
                    safeText(o?.paymentInfo?.paymentMethod || o?.paymentMethod) || "—";

                  const totalValue = Number(o?.totalPrice ?? o?.totalPriceAfterDiscount ?? 0) || 0;

                  const canCancelRow = (() => {
                    const st = status.toLowerCase();
                    if (st === "cancelled" || st === "canceled") return false;
                    if (st === "delivered") return false;
                    return true;
                  })();

                  const cancelRow = async () => {
                    if (!oid) return;

                    const ok = window.confirm(
                      "Cancel this order?\n\nThis action will mark the order as cancelled."
                    );
                    if (!ok) return;

                    try {
                      setCancellingId(String(oid));
                      await dispatch(cancelMyOrder(String(oid))).unwrap();
                      await dispatch(getOrders()).unwrap();
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setCancellingId("");
                    }
                  };

                  return (
                    <div className="oc-rRow" key={oid || o?.orderNumber || o?.orderId || dateLabel}>
                      <div className="oc-rOrder">
                        <div className="oc-rId">
                          {o?.orderNumber
                            ? `#${String(o.orderNumber).replace(/^#/, "")}`
                            : oid
                            ? `#${String(oid).slice(-8)}`
                            : "—"}
                        </div>
                        <div className="oc-rMeta">
                          {itemsCount} item{itemsCount === 1 ? "" : "s"} • {method}
                        </div>
                      </div>

                      <div className="oc-rCell">{dateLabel}</div>

                      <div className="oc-rCell">
                        <span
                          className={`oc-badge ${
                            status.toLowerCase() === "delivered"
                              ? "delivered"
                              : status.toLowerCase() === "cancelled"
                              ? "cancelled"
                              : "processing"
                          }`}
                        >
                          {status}
                        </span>
                      </div>

                      <div className="oc-rCell oc-rRight">{formatUGX(totalValue)}</div>

                      <div className="oc-rCell oc-rRight">
                        <button
                          type="button"
                          className="oc-rBtn"
                          onClick={() => viewOrder(oid)}
                          disabled={!oid}
                        >
                          View
                        </button>

                        <button
                          type="button"
                          className="oc-rBtn oc-rBtn--ghost"
                          onClick={() => reorder(oid)}
                          disabled={!oid || String(reorderingId) === String(oid)}
                        >
                          {String(reorderingId) === String(oid) ? "Reordering..." : "Reorder"}
                        </button>

                        {/* ✅ CANCEL (Recent Orders) */}
                        <button
                          type="button"
                          className="oc-rBtn oc-rBtn--ghost"
                          onClick={cancelRow}
                          disabled={!canCancelRow || String(cancellingId) === String(oid)}
                          title={!canCancelRow ? "Cannot cancel this order" : "Cancel order"}
                          style={{ marginLeft: 8, opacity: !canCancelRow ? 0.6 : 1 }}
                        >
                          {String(cancellingId) === String(oid) ? "Cancelling..." : "Cancel"}
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: 16, color: "#777", fontSize: 13 }}>
                  No recent orders yet.
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Orders;